import { colors } from 'theme/colors';
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from 'components/common/HoverTooltip';
import { CopyToClipboard } from 'components/common/ProjectIcons';
import { Icon } from 'react-icons-kit';
import { ic_mode_edit } from 'react-icons-kit/md/ic_mode_edit';
import { ic_filter_list } from 'react-icons-kit/md/ic_filter_list';
import { ic_delete_outline } from 'react-icons-kit/md/ic_delete_outline';
import { ic_search } from 'react-icons-kit/md/ic_search';
import { copyToClipboard } from 'components/common/copyToClipboard';
import Editor from '@monaco-editor/react';
import { MONO_FONT_FAMILY } from 'fonts';
import type { editor } from 'monaco-editor';
import {
  inputClasses,
  buttonClasses,
} from 'components/common/tailwindClassnames';
import RequiredTag from 'components/common/RequiredTag';
import { CodeBuilderToggle } from 'components/common/CodeBuilderToggle';
import { GUNDI_OBJECT_SPECS } from './schema-builder/gundiObjectSpecs';
import { validateMappedOutput } from './schema-builder/validation';
import { SelectMenu } from './schema-builder/SelectMenu';
import { registerJqLanguage } from './jqLanguage';
import {
  CoreFieldMaps,
  AdditionalField,
  Condition,
  OPS,
  ParseResult,
  Transform,
  TransformMap,
} from './jq-filter-builder/types';
import {
  jqPath,
  valAt,
  flatPaths,
  genJq,
  evalMapping,
  parseJqToBuilderState,
  applyTransformsJs,
  formatPreviewText,
  normalizeTransforms,
} from './jq-filter-builder/helpers';
import {
  TransformAddButton,
  TransformPills,
} from './jq-filter-builder/TransformControls';
import {
  MappingChip,
  EmptySlot,
  LockedChip,
} from './jq-filter-builder/SlotParts';
import { ic_close } from 'react-icons-kit/md/ic_close';
import {
  PANEL_CLASSES,
  HEADER_CLASSES,
  SECTION_LABEL,
  SLOT_LABEL,
  SLOT_ROW,
} from './jq-filter-builder/styles';

// ── Drag-and-drop helpers ──

type DragPathRef = React.MutableRefObject<string[] | null>;

// Keyboard/click-mapping handshake: a drop slot calls this on Enter or click to hand its
// "pick" callback to the parent, which then moves focus into the source tree. Selecting a
// leaf (Enter or click) invokes the callback with that leaf's path. Mirrors the
// drag-and-drop flow for keyboards and for slots scrolled out of drag reach.
type StartPick = (
  onPick: (path: string[]) => void,
  slotEl: HTMLElement,
) => void;

// Applied imperatively to the armed slot while a pick is in progress (the slot's own
// render doesn't know it is the pending one — the pick state lives on the parent).
// slot-armed turns the dashed outline teal (index.css); the classes go on the
// slot's dashed wrapper, not the inner target the pick was started from.
const ARMED_SLOT_CLASSES = ['slot-armed', 'animate-pulse'];
const armTarget = (el: HTMLElement | null | undefined) =>
  (el?.closest('[data-empty-slot]') as HTMLElement | null) ?? el ?? null;

// The Code editor's floor, and the height the panel has always had. It grows
// from here to whatever the filter needs, so a short filter looks the same as
// before and a long one is never scrolled inside its own box.
const CODE_EDITOR_MIN_HEIGHT = 360;

function useDragOver(readOnly: boolean) {
  const [isOver, setIsOver] = useState(false);

  const handlers = readOnly
    ? {}
    : {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setIsOver(true);
        },
        onDragLeave: () => setIsOver(false),
      };

  const resetOver = () => setIsOver(false);

  return { isOver, handlers, resetOver };
}

// ── Sub-components ──

function TreeNode({
  name,
  value,
  path,
  depth,
  collapsed,
  onToggle,
  readOnly,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPathKey,
  mappedTo,
  visiblePaths,
}: {
  name: string;
  value: any;
  path: string[];
  depth: number;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onLeafActivate?: (path: string[]) => void;
  pickActive?: boolean;
  // Path key of the leaf armed for reverse (source-first) click-mapping, if any.
  armedPathKey?: string;
  // Path key → labels of the slots that use it, so a leaf can show "→ source".
  mappedTo?: Record<string, string[]>;
  // Path keys left visible by the field filter (null = no filter active).
  visiblePaths?: Set<string> | null;
}) {
  const isObject =
    value != null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isLeaf = !isObject && !isArray;
  const pathKey = path.join('.');
  const filtering = visiblePaths != null;
  // While filtering, keep branches open so matches are visible.
  const isCollapsed = !filtering && collapsed.has(pathKey);
  const targets = mappedTo?.[pathKey];
  const hasChildren = isObject
    ? Object.keys(value as object).length > 0
    : isArray
      ? (value as any[]).length > 0
      : false;

  const typeColor =
    typeof value === 'number'
      ? colors['unlisted-json-number-blue']
      : typeof value === 'string'
        ? colors['ui-cta-primary']
        : typeof value === 'boolean'
          ? colors['unlisted-orange']
          : colors['unlisted-medium-gray'];

  const displayValue = isObject
    ? `{${Object.keys(value as object).length}}`
    : isArray
      ? `[${(value as any[]).length}]`
      : JSON.stringify(value);

  const ghostRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = (e: React.DragEvent) => {
    if (!isLeaf || readOnly) return;
    dragPathRef.current = path;
    e.dataTransfer.setData('text/plain', pathKey);
    e.dataTransfer.effectAllowed = 'copy';

    const ghost = document.createElement('div');
    ghost.textContent = jqPath(path);
    ghost.style.cssText = `position:absolute;top:-100px;left:-100px;padding:4px 10px;background:${colors['ui-cta-primary']};color:${colors['white']};border-radius:6px;font:12px ${MONO_FONT_FAMILY};white-space:nowrap;`;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 0, 0);
  };

  const handleDragEnd = () => {
    dragPathRef.current = null;
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      ghostRef.current?.remove();
    };
  }, []);

  // After the hooks: a filtered-out node renders nothing.
  if (filtering && !visiblePaths.has(pathKey)) return null;

  return (
    <div>
      <div
        draggable={isLeaf && !readOnly}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        tabIndex={isLeaf && !readOnly ? (pickActive ? 0 : -1) : undefined}
        role={isLeaf && !readOnly ? 'button' : undefined}
        aria-label={isLeaf && !readOnly ? jqPath(path) : undefined}
        data-leaf-focusable={isLeaf && !readOnly ? 'true' : undefined}
        onKeyDown={
          isLeaf && !readOnly
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLeafActivate?.(path);
                }
              }
            : undefined
        }
        onClick={isLeaf && !readOnly ? () => onLeafActivate?.(path) : undefined}
        className={`flex select-none items-center gap-1 rounded px-1.5 py-0.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ui-cta-primary ${isLeaf && !readOnly ? `${pickActive ? 'cursor-pointer' : 'cursor-grab'} hover:bg-unlisted-light-blue` : 'cursor-default'} ${armedPathKey === pathKey ? 'bg-unlisted-light-blue ring-2 ring-inset ring-ui-cta-primary' : ''}`}
        style={{ paddingLeft: depth * 16 + 6 }}
      >
        {hasChildren ? (
          // Inert while filtering: branches are forced open, so a toggle
          // would change nothing visible until the filter is cleared.
          <span
            onClick={filtering ? undefined : () => onToggle(pathKey)}
            className={`w-3.5 text-center text-[10px] text-ui-text-primary-text ${filtering ? 'cursor-default opacity-40' : 'cursor-pointer'}`}
          >
            {isCollapsed ? '▶' : '▼'}
          </span>
        ) : (
          <span
            className="w-3.5 text-center text-[10px]"
            style={{ color: colors['unlisted-json-key-blue'] }}
          >
            ⠿
          </span>
        )}
        <span className="font-medium text-ui-text-primary-text">{name}</span>
        {isLeaf && (
          <span
            className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ color: typeColor }}
          >
            {displayValue}
          </span>
        )}
        {!isLeaf && (
          <span className="text-[11px] text-ui-text-primary-text">
            {displayValue}
          </span>
        )}
        {isLeaf && targets && targets.length > 0 && (
          <span
            className="ml-auto min-w-0 max-w-[45%] shrink truncate whitespace-nowrap pl-2 font-sans text-[11px] text-ui-cta-primary"
            title={targets.join(', ')}
          >
            → {targets.join(', ')}
          </span>
        )}
      </div>
      {isObject &&
        !isCollapsed &&
        Object.entries(value as Record<string, any>).map(
          ([childKey, childValue]) => (
            <TreeNode
              key={childKey}
              name={childKey}
              value={childValue}
              path={[...path, childKey]}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onLeafActivate={onLeafActivate}
              pickActive={pickActive}
              armedPathKey={armedPathKey}
              mappedTo={mappedTo}
              visiblePaths={visiblePaths}
            />
          ),
        )}
      {isArray &&
        !isCollapsed &&
        (value as any[]).map((item, index) => (
          <TreeNode
            key={index}
            name={`[${index}]`}
            value={item}
            path={[...path, String(index)]}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            readOnly={readOnly}
            dragPathRef={dragPathRef}
            onLeafActivate={onLeafActivate}
            pickActive={pickActive}
            armedPathKey={armedPathKey}
            mappedTo={mappedTo}
            visiblePaths={visiblePaths}
          />
        ))}
    </div>
  );
}

function DropSlot({
  label,
  mapping,
  sampleInput,
  onDrop,
  onClear,
  readOnly,
  dragPathRef,
  required,
  onStartPick,
  sourceArmed,
  transforms,
  onChangeTransforms,
  transformsLocked,
}: {
  label: string;
  mapping: string[] | null;
  sampleInput: any;
  onDrop: (path: string[]) => void;
  onClear: () => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  required?: boolean;
  onStartPick?: StartPick;
  // A source leaf is armed for click-mapping — highlight the empty zone as a hot target.
  sourceArmed?: boolean;
  transforms?: Transform[];
  onChangeTransforms?: (next: Transform[]) => void;
  // Transform edits regenerate jq; while the filter is complex the builder never pushes
  // regenerated jq to the parent, so editing here would silently not save.
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const previewValue = mapping ? valAt(sampleInput, mapping) : undefined;
  const pathNotInSample = mapping && previewValue === undefined;
  // Chip shows the RAW source value; the transformed result renders after the pills,
  // so the row reads before → after.
  const transformedValue = applyTransformsJs(
    previewValue,
    transforms,
    sampleInput,
  );
  const { t } = useTranslation();

  const wand =
    !readOnly && onChangeTransforms ? (
      <TransformAddButton
        onAdd={(tr) => onChangeTransforms([...(transforms ?? []), tr])}
        disabled={transformsLocked}
        // Only an empty slot restricts the kinds: with no base value, just a
        // hardcoded default or a fallback field can supply one.
        allowedKinds={
          mapping || transforms?.some((tr) => tr.kind === 'coalesce')
            ? undefined
            : ['default', 'coalesce']
        }
      />
    ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      <span className={SLOT_LABEL}>
        {label}
        {required && <RequiredTag size="sm" />}
      </span>
      {mapping ? (
        <MappingChip
          path={mapping}
          previewValue={previewValue}
          notInSample={!!pathNotInSample}
          readOnly={readOnly}
          onClear={onClear}
          clearLabel={t('Clear mapping')}
          wand={wand}
        />
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(onDrop, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) onDrop(dragPathRef.current);
          }}
          wand={wand}
        />
      )}
      {transforms && transforms.length > 0 && onChangeTransforms && (
        <TransformPills
          transforms={transforms}
          onChange={onChangeTransforms}
          readOnly={readOnly || transformsLocked}
          previewText={formatPreviewText(transformedValue)}
        />
      )}
    </div>
  );
}

function AdditionalRow({
  field,
  index,
  onUpdate,
  onRemove,
  sampleInput,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
  transformsLocked,
}: {
  field: AdditionalField;
  index: number;
  onUpdate: (index: number, field: AdditionalField) => void;
  onRemove: (index: number) => void;
  sampleInput: any;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
  transformsLocked?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const pathSegments = field.path ? field.path.split('.') : null;
  const previewValue = pathSegments
    ? valAt(sampleInput, pathSegments)
    : undefined;
  const pathNotInSample = field.path && previewValue === undefined;
  const transformedValue = applyTransformsJs(
    previewValue,
    field.transforms,
    sampleInput,
  );
  const setFieldTransforms = (next: Transform[]) =>
    onUpdate(index, {
      ...field,
      transforms: next.length ? normalizeTransforms(next) : undefined,
    });
  const { t } = useTranslation();

  const setPath = (p: string[]) =>
    onUpdate(index, { ...field, path: p.join('.') });

  const wand = !readOnly ? (
    <TransformAddButton
      onAdd={(tr) => setFieldTransforms([...(field.transforms ?? []), tr])}
      disabled={transformsLocked}
      allowedKinds={
        field.path || field.transforms?.some((tr) => tr.kind === 'coalesce')
          ? undefined
          : ['default', 'coalesce']
      }
    />
  ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      {/* The trash beside the name removes the whole row; the chip's × below
          only clears the mapping, like a core slot. */}
      <div className="mb-1.5 flex items-center gap-2">
        <input
          value={field.name}
          onChange={(e) => onUpdate(index, { ...field, name: e.target.value })}
          placeholder={t('field name')}
          disabled={readOnly}
          aria-label={t('field name')}
          className={`${inputClasses.standard} min-w-0 flex-1 font-mono text-xs`}
        />
        {!readOnly && (
          <HoverTooltip
            content={t('Remove field')}
            position="top"
            focusable={false}
            portal
          >
            <button
              type="button"
              onClick={() => onRemove(index)}
              aria-label={t('Remove field')}
              className="flex shrink-0 items-center text-greyscale-5 hover:text-ui-status-error-destructive"
            >
              <Icon icon={ic_delete_outline} size={20} />
            </button>
          </HoverTooltip>
        )}
      </div>
      {pathSegments ? (
        <MappingChip
          path={pathSegments}
          previewValue={previewValue}
          notInSample={!!pathNotInSample}
          readOnly={readOnly}
          onClear={() => onUpdate(index, { ...field, path: '' })}
          clearLabel={t('Clear mapping')}
          wand={wand}
        />
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(setPath, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) setPath(dragPathRef.current);
          }}
          wand={wand}
        />
      )}
      {field.transforms && field.transforms.length > 0 && (
        <TransformPills
          transforms={field.transforms}
          onChange={setFieldTransforms}
          readOnly={readOnly || transformsLocked}
          previewText={formatPreviewText(transformedValue)}
        />
      )}
    </div>
  );
}

function ConditionRow({
  condition,
  index,
  onUpdate,
  onRemove,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
}: {
  condition: Condition;
  index: number;
  onUpdate: (index: number, condition: Condition) => void;
  onRemove: (index: number) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const { t } = useTranslation();
  const requiresValue = !['not_null', 'is_null'].includes(condition.op);
  const setField = (p: string[]) =>
    onUpdate(index, { ...condition, field: p.join('.') });
  const fieldPath = condition.field ? condition.field.split('.') : null;

  // Built from the same parts as a field row: the teal chip holds the path
  // with the divider and ×; the operator and its value sit on the line below.
  // The × removes the whole condition — one without a field is meaningless.
  const removeButton = !readOnly ? (
    <button
      type="button"
      onClick={() => onRemove(index)}
      aria-label={t('Remove condition')}
      className="flex shrink-0 items-center px-1 text-ui-text-primary-text hover:text-ui-cta-primary"
    >
      <Icon icon={ic_close} size={18} />
    </button>
  ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      {fieldPath ? (
        <>
          <MappingChip
            path={fieldPath}
            showPreview={false}
            notInSample={false}
            readOnly={readOnly}
            onClear={() => onRemove(index)}
            clearLabel={t('Remove condition')}
          />
          <div className="mt-1.5 flex items-center gap-2">
            <SelectMenu
              value={condition.op}
              options={OPS.map((op) => ({
                value: op.v,
                label: t(op.labelKey as any),
              }))}
              onChange={(v) => onUpdate(index, { ...condition, op: v })}
              disabled={readOnly}
              className="w-40 shrink-0"
              compact
              ariaLabel={t('Operator')}
            />
            {requiresValue && (
              <input
                value={condition.value || ''}
                onChange={(e) =>
                  onUpdate(index, { ...condition, value: e.target.value })
                }
                placeholder={t('value')}
                disabled={readOnly}
                aria-label={t('value')}
                className={`${inputClasses.standard} min-w-0 flex-1 py-1 font-mono text-xs`}
              />
            )}
          </div>
        </>
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(setField, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) setField(dragPathRef.current);
          }}
          extra={removeButton}
        />
      )}
    </div>
  );
}

// The Source Payload tree with its "Filter fields…" box. The filter text is
// local state here, so typing redraws only the tree, not the mappings panel.
function SourceTree({
  sampleInput,
  readOnly,
  treeRef,
  onEscape,
  collapsed,
  onToggle,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPathKey,
  mappedTo,
}: {
  sampleInput: any;
  readOnly: boolean;
  treeRef: React.RefObject<HTMLDivElement>;
  onEscape: (e: React.KeyboardEvent) => void;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  dragPathRef: DragPathRef;
  onLeafActivate: (path: string[]) => void;
  pickActive: boolean;
  armedPathKey?: string;
  mappedTo: Record<string, string[]>;
}) {
  const { t } = useTranslation();
  const [treeFilter, setTreeFilter] = useState('');
  const normalizedFilter = treeFilter.trim().toLowerCase();
  const isEmpty = Object.keys(sampleInput).length === 0;

  // One pass over the sample: a node stays visible when its path, or any
  // descendant's path, contains the filter text.
  const visiblePaths = useMemo(() => {
    if (!normalizedFilter) return null;
    const visible = new Set<string>();
    const walk = (value: any, path: string): boolean => {
      let hit = path.toLowerCase().includes(normalizedFilter);
      if (value && typeof value === 'object') {
        Object.entries(value).forEach(([k, child]) => {
          if (walk(child, path ? `${path}.${k}` : k)) hit = true;
        });
      }
      if (hit && path) visible.add(path);
      return hit;
    };
    walk(sampleInput, '');
    return visible;
  }, [sampleInput, normalizedFilter]);

  return (
    <>
      {!isEmpty && (
        <div className="border-b border-ui-divider-lines px-3.5 py-2.5">
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-greyscale-5">
              <Icon icon={ic_search} size={16} />
            </span>
            <input
              type="search"
              value={treeFilter}
              onChange={(e) => setTreeFilter(e.target.value)}
              placeholder={t('Filter fields…')}
              aria-label={t('Filter fields…')}
              data-testid="JqFilterBuilder-filter-input"
              className={`${inputClasses.standard} w-full pl-8 text-sm`}
            />
          </div>
        </div>
      )}
      <div
        ref={treeRef}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onEscape(e);
        }}
        className={`flex-1 overflow-y-auto py-1.5 ${pickActive ? 'rounded ring-2 ring-inset ring-ui-cta-primary' : ''}`}
      >
        {isEmpty ? (
          <div className="px-3.5 py-2 text-xs italic text-ui-text-primary-text">
            {!readOnly
              ? t('Use the pencil to add a sample payload')
              : t('No sample payload')}
          </div>
        ) : (
          Object.entries(sampleInput).map(([key, value]) => (
            <TreeNode
              key={key}
              name={key}
              value={value}
              path={[key]}
              depth={0}
              collapsed={collapsed}
              onToggle={onToggle}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onLeafActivate={onLeafActivate}
              pickActive={pickActive}
              armedPathKey={armedPathKey}
              mappedTo={mappedTo}
              visiblePaths={visiblePaths}
            />
          ))
        )}
      </div>
    </>
  );
}

function LockedFieldSlot({
  label,
  expression,
  required,
  readOnly,
}: {
  label: string;
  expression: string;
  required?: boolean;
  readOnly: boolean;
}) {
  return (
    <div className="mb-4">
      <span className={SLOT_LABEL}>
        {label}
        {required && <RequiredTag size="sm" />}
      </span>
      <LockedChip
        expression={expression}
        // Disabled on purpose: its tooltip explains the field is edited in
        // Code mode.
        wand={
          readOnly ? undefined : (
            <TransformAddButton onAdd={() => {}} disabled />
          )
        }
      />
    </div>
  );
}

// ── Main component ──

export type JqFilterBuilderProps = {
  onGenerate: (jq: string) => void;
  onCodeChange?: (jq: string) => void;
  onSampleInputChange?: (sample: object) => void;
  readOnly?: boolean;
  initialJq?: string;
  initialSampleInput?: object;
  objectType?: string;
};

const JqFilterBuilder = ({
  onGenerate,
  onCodeChange,
  onSampleInputChange,
  readOnly = false,
  initialJq,
  initialSampleInput,
  objectType = 'obv',
}: JqFilterBuilderProps) => {
  const { t } = useTranslation();
  const dragPathRef = useRef<string[] | null>(null);
  const jqEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const hasInteracted = useRef(false);
  const codeEditedByUser = useRef(false);
  const codeSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onGenerateRef = useRef(onGenerate);
  onGenerateRef.current = onGenerate;

  // ── Keyboard mapping (a11y) ──
  // Mirrors drag-and-drop for keyboard users: pressing Enter on a drop slot hands its
  // "pick" callback here and moves focus to the first source leaf; pressing Enter on a
  // leaf invokes that callback with the leaf's path. Esc (handled on the tree) cancels.
  const sourceTreeRef = useRef<HTMLDivElement>(null);
  const pendingPickRef = useRef<((path: string[]) => void) | null>(null);
  const lastSlotRef = useRef<HTMLElement | null>(null);
  const [pickActive, setPickActive] = useState(false);

  // Reverse (source-first) pick: clicking a leaf arms its path; the next slot click
  // completes the mapping. Ref mirrors the state so the same-click document listener
  // and startKeyboardPick see completion synchronously, before React re-renders.
  const sourceArmedRef = useRef<string[] | null>(null);
  const [sourceArmedPath, setSourceArmedPath] = useState<string[] | null>(null);

  const cancelSourcePick = useCallback(() => {
    if (!sourceArmedRef.current) return;
    sourceArmedRef.current = null;
    setSourceArmedPath(null);
  }, []);

  const startKeyboardPick = useCallback<StartPick>((onPick, slotEl) => {
    // A source leaf is already armed — complete the mapping right away.
    if (sourceArmedRef.current) {
      const path = sourceArmedRef.current;
      sourceArmedRef.current = null;
      setSourceArmedPath(null);
      onPick(path);
      return;
    }
    const firstLeaf = sourceTreeRef.current?.querySelector<HTMLElement>(
      '[data-leaf-focusable="true"]',
    );
    if (!firstLeaf) return; // no selectable source fields — nothing to pick
    pendingPickRef.current = onPick;
    lastSlotRef.current = slotEl;
    armTarget(slotEl)?.classList.add(...ARMED_SLOT_CLASSES);
    setPickActive(true);
    firstLeaf.focus();
  }, []);

  const completeKeyboardPick = useCallback((path: string[]) => {
    const pick = pendingPickRef.current;
    if (!pick) return;
    // The drop zone unmounts once mapped (replaced by the mapping display), so return
    // focus to its persistent row wrapper instead — keeping the user on the right side.
    const slotEl = lastSlotRef.current;
    const row =
      (slotEl?.closest('[data-slot-row]') as HTMLElement | null) ?? slotEl;
    armTarget(slotEl)?.classList.remove(...ARMED_SLOT_CLASSES);
    pendingPickRef.current = null;
    lastSlotRef.current = null;
    setPickActive(false);
    pick(path);
    row?.focus();
  }, []);

  const cancelKeyboardPick = useCallback(() => {
    if (!pendingPickRef.current) return;
    pendingPickRef.current = null;
    setPickActive(false);
    armTarget(lastSlotRef.current)?.classList.remove(...ARMED_SLOT_CLASSES);
    lastSlotRef.current?.focus();
    lastSlotRef.current = null;
  }, []);

  // Leaf activation (Enter or click): completes a slot-first pick if one is pending,
  // otherwise toggles the leaf as the armed source for a source-first pick.
  const activateLeaf = useCallback(
    (path: string[]) => {
      if (pendingPickRef.current) {
        completeKeyboardPick(path);
        return;
      }
      if (sourceArmedRef.current?.join('.') === path.join('.')) {
        cancelSourcePick();
        return;
      }
      sourceArmedRef.current = path;
      setSourceArmedPath(path);
    },
    [completeKeyboardPick, cancelSourcePick],
  );

  // Click-away cancels an active slot-first pick. `mousedown` (not `click`) so that
  // clicking a different empty slot cancels first, then that slot's own click re-arms
  // the pick. Clicks inside the source tree are left to the leaf onClick handlers.
  useEffect(() => {
    if (!pickActive) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (sourceTreeRef.current?.contains(e.target as Node)) return;
      cancelKeyboardPick();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [pickActive, cancelKeyboardPick]);

  // Click-away cancels an armed source leaf. `click` (not `mousedown`) so a slot's own
  // click handler runs first and completes the mapping (clearing the ref) before this
  // fires; the listener is attached after the arming click has already finished.
  useEffect(() => {
    if (!sourceArmedPath) return;
    const onDocClick = (e: MouseEvent) => {
      if (!sourceArmedRef.current) return; // completed by a slot click
      if (sourceTreeRef.current?.contains(e.target as Node)) return; // toggling/re-arming a leaf
      cancelSourcePick();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [sourceArmedPath, cancelSourcePick]);

  // ── Source payload state ──
  const [sampleInput, setSampleInput] = useState<any>(initialSampleInput ?? {});
  const [rawJsonInput, setRawJsonInput] = useState('');
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // ── Builder state (initialized from existing jq if available) ──
  const [parsedInitial] = useState<ParseResult | null>(() =>
    parseJqToBuilderState(initialJq ?? ''),
  );
  const [isComplexJq, setIsComplexJq] = useState(
    () => parsedInitial != null && !parsedInitial.isFullyParseable,
  );
  const [complexFields, setComplexFields] = useState<Record<string, string>>(
    () => parsedInitial?.complexFields ?? {},
  );

  const [coreFieldMaps, setCoreFieldMaps] = useState<CoreFieldMaps>(
    () => parsedInitial?.maps ?? {},
  );
  const [transforms, setTransforms] = useState<TransformMap>(
    () => parsedInitial?.transforms ?? {},
  );
  const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>(
    () => {
      const parsed = parsedInitial?.addl ?? [];
      return parsed.length > 0 ? parsed : [{ name: '', path: '' }];
    },
  );
  const [conditions, setConditions] = useState<Condition[]>(
    () => parsedInitial?.conds ?? [],
  );

  // ── Code editor state (auto-switch to Code for complex jq) ──
  const [isCodeMode, setIsCodeMode] = useState(() => isComplexJq);
  const [codeText, setCodeText] = useState(initialJq ?? '');
  const [codeEditorHeight, setCodeEditorHeight] = useState<number>(
    CODE_EDITOR_MIN_HEIGHT,
  );

  // ── Sync initial sample input from props ──
  useEffect(() => {
    if (initialSampleInput && Object.keys(initialSampleInput).length > 0) {
      setSampleInput(initialSampleInput);
    }
  }, [initialSampleInput]);

  // Core and additional field mappings keep their references when the sample loses a path —
  // DropSlot / AdditionalRow surface a "Not in schema" warning so the user can decide whether
  // to clear them. Conditions don't have an equivalent affordance, so they are still cleared.
  useEffect(() => {
    if (Object.keys(sampleInput).length === 0) return;
    const validPaths = new Set(
      flatPaths(sampleInput).map((p) => p.path.join('.')),
    );

    setConditions((prev) => {
      const next = prev.map((c) =>
        c.field && !validPaths.has(c.field) ? { ...c, field: '' } : c,
      );
      return next.some((c, i) => c !== prev[i]) ? next : prev;
    });
  }, [sampleInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ──
  const generatedJq = useMemo(
    () =>
      genJq(
        conditions,
        coreFieldMaps,
        additionalFields,
        objectType,
        transforms,
      ),
    [conditions, coreFieldMaps, additionalFields, objectType, transforms],
  );
  const evalResult = useMemo(
    () =>
      evalMapping(
        sampleInput,
        coreFieldMaps,
        additionalFields,
        conditions,
        objectType,
        complexFields,
        transforms,
      ),
    [
      sampleInput,
      coreFieldMaps,
      additionalFields,
      conditions,
      objectType,
      complexFields,
      transforms,
    ],
  );
  const outputPreview = evalResult.data ? [evalResult.data] : [];
  const outputErrors = useMemo(() => {
    if (!evalResult.data) return [];
    return validateMappedOutput(evalResult.data, objectType);
  }, [evalResult.data, objectType]);

  // ── Notify parent when jq changes (only after user interaction, never for complex jq) ──
  useEffect(() => {
    if (hasInteracted.current && !isComplexJq)
      onGenerateRef.current(generatedJq);
  }, [generatedJq, isComplexJq]);

  // ── Object type spec & optional fields ──
  const spec = GUNDI_OBJECT_SPECS[objectType];
  const optionalFields = useMemo(
    () => (spec ? spec.fields.filter((f) => !f.required) : []),
    [spec],
  );
  const [activeOptionalKeys, setActiveOptionalKeys] = useState<Set<string>>(
    () => {
      const initial = new Set<string>();
      for (const f of optionalFields) {
        if (coreFieldMaps[f.key] || transforms[f.key]?.length)
          initial.add(f.key);
      }
      return initial;
    },
  );

  // Only prune coreFieldMaps when the user CHANGES objectType, not on initial mount.
  // On initial mount the maps come from the parser and may contain valid fields
  // not in the current spec (e.g. source in an event jq, or title in an observation jq).
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Still seed activeOptionalKeys from initial maps
      const nextActiveOptionalKeys = new Set<string>();
      for (const f of optionalFields) {
        if (coreFieldMaps[f.key] || transforms[f.key]?.length)
          nextActiveOptionalKeys.add(f.key);
      }
      setActiveOptionalKeys(nextActiveOptionalKeys);
      return;
    }

    if (!spec) {
      setActiveOptionalKeys(new Set());
      setCoreFieldMaps(() => ({}) as CoreFieldMaps);
      setTransforms({});
      return;
    }

    // Clear core fields, transforms, and conditions that don't belong to the new object
    // type. Additional fields are NOT touched — they are user-defined and type-independent,
    // and their transforms live on the field objects, so they survive the type change too.
    markInteracted();
    setCoreFieldMaps({} as CoreFieldMaps);
    setTransforms({});
    setConditions([]);
    setActiveOptionalKeys(new Set());
  }, [objectType, spec, optionalFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync code editor when generatedJq changes (e.g. after objectType prune)
  useEffect(() => {
    if (!isComplexJq && !codeEditedByUser.current) setCodeText(generatedJq);
  }, [generatedJq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync builder state from code editor edits (debounced to avoid parsing mid-keystroke)
  useEffect(() => {
    if (!codeEditedByUser.current) return;
    codeEditedByUser.current = false;

    if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    codeSyncTimer.current = setTimeout(() => {
      const parsed = parseJqToBuilderState(codeText);
      if (!parsed) {
        // Empty or fully unparseable — clear builder
        setCoreFieldMaps({} as CoreFieldMaps);
        setTransforms({});
        setAdditionalFields([{ name: '', path: '' }]);
        setConditions([]);
        setComplexFields({});
        setIsComplexJq(false);
        return;
      }
      setCoreFieldMaps(parsed.maps);
      setTransforms(parsed.transforms);
      setConditions(parsed.conds);
      setAdditionalFields(
        parsed.addl.length > 0 ? parsed.addl : [{ name: '', path: '' }],
      );
      setComplexFields(parsed.complexFields);
      setIsComplexJq(!parsed.isFullyParseable);
    }, 400);

    return () => {
      if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
    };
  }, [codeText, objectType]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeOptionalFields = optionalFields.filter((f) =>
    activeOptionalKeys.has(f.key),
  );

  // Path key → labels of the slots that use it, so the tree can show "→ source".
  const mappedTo = useMemo(() => {
    // Null prototype: a sample key such as `constructor` or `toString` must
    // not resolve to an inherited Object property.
    const out: Record<string, string[]> = Object.create(null);
    const add = (key: string | null | undefined, label: string) => {
      if (!key) return;
      (out[key] ??= []).push(label);
    };
    Object.entries(coreFieldMaps).forEach(([field, path]) =>
      add(path?.join('.'), field),
    );
    // An unnamed row has no target name to show yet.
    additionalFields.forEach((f) => f.name && add(f.path, f.name));
    return out;
  }, [coreFieldMaps, additionalFields]);

  const hasMappings =
    Object.values(coreFieldMaps).some((v) => v != null) ||
    additionalFields.some((f) => f.name && (f.path || f.transforms?.length)) ||
    Object.values(transforms).some((chain) => (chain?.length ?? 0) > 0);

  // ── Interaction helpers ──
  const markInteracted = () => {
    hasInteracted.current = true;
  };

  const updateCoreFieldMap = (key: string, path: string[] | null) => {
    markInteracted();
    setCoreFieldMaps((prev) => ({ ...prev, [key]: path }));
    // Clearing a mapping drops any transforms attached to it.
    if (path === null) setTransformsFor(key, []);
  };

  // Set (or clear, when empty) the transforms attached to a core field key.
  const setTransformsFor = (key: string, next: Transform[]) => {
    markInteracted();
    setTransforms((prev) => {
      if (!next.length && !(key in prev)) return prev; // no-op delete — keep identity
      const updated = { ...prev };
      if (next.length) updated[key] = normalizeTransforms(next);
      else delete updated[key];
      return updated;
    });
  };

  const addCondition = () => {
    markInteracted();
    setConditions((prev) => [
      ...prev,
      { field: '', op: 'not_null', value: '' },
    ]);
  };
  const updateCondition = (index: number, updated: Condition) => {
    markInteracted();
    setConditions((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };
  const removeCondition = (index: number) => {
    markInteracted();
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const addAdditionalField = () => {
    markInteracted();
    setAdditionalFields((prev) => [...prev, { name: '', path: '' }]);
  };
  const updateAdditionalField = (index: number, updated: AdditionalField) => {
    markInteracted();
    // Transforms live on the field object, so they travel through renames for free.
    // Only CLEARING a mapped path drops them (mirrors core fields) — a path-less field
    // keeps its transforms, since they are what carries its value.
    setAdditionalFields((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        return f.path && !updated.path
          ? { ...updated, transforms: undefined }
          : updated;
      }),
    );
  };
  const removeAdditionalField = (index: number) => {
    markInteracted();
    setAdditionalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const activateOptionalField = (key: string) => {
    markInteracted();
    setActiveOptionalKeys((prev) => new Set([...prev, key]));
  };
  const deactivateOptionalField = (key: string) => {
    markInteracted();
    setActiveOptionalKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setCoreFieldMaps((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setTransforms((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleCollapsed = useCallback(
    (key: string) =>
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      }),
    [],
  );

  const applyJsonEdit = () => {
    try {
      const parsed = JSON.parse(rawJsonInput);
      setSampleInput(parsed);
      onSampleInputChange?.(parsed);
      setJsonParseError(null);
      setIsEditingJson(false);
    } catch {
      setJsonParseError(t('Invalid JSON'));
    }
  };

  const toggleCodeMode = () => {
    const next = !isCodeMode;
    if (next) setCodeText(isComplexJq ? (initialJq ?? '') : generatedJq);
    setIsCodeMode(next);
  };

  // Complex fields are individually locked via LockedFieldSlot; simple fields remain editable

  // ── Render ──
  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-2 gap-3">
        {/* ── Source Payload Panel ── */}
        <div className={PANEL_CLASSES}>
          <div className={HEADER_CLASSES}>
            <span>{t('Source Payload')}</span>
            {/* The panel is overflow-hidden, so the tooltip must portal out
                or it is clipped by the header. */}
            {!readOnly && !isEditingJson && (
              <HoverTooltip
                content={t('Edit sample payload')}
                position="top"
                focusable={false}
                portal
              >
                <button
                  type="button"
                  data-testid="JqFilterBuilder-edit-sample-button"
                  aria-label={t('Edit sample payload')}
                  onClick={() => {
                    setIsEditingJson(true);
                    setRawJsonInput(JSON.stringify(sampleInput, null, 2));
                  }}
                  className="text-greyscale-5 hover:text-ui-text-primary-text"
                >
                  <Icon icon={ic_mode_edit} size={18} />
                </button>
              </HoverTooltip>
            )}
            {!readOnly && isEditingJson && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsEditingJson(false);
                    setJsonParseError(null);
                  }}
                  className={buttonClasses.outlineXs}
                  type="button"
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  data-testid="JqFilterBuilder-save-sample-button"
                  onClick={applyJsonEdit}
                  className={buttonClasses.outlineXs}
                >
                  {t('Save')}
                </button>
              </div>
            )}
          </div>
          {isEditingJson && (
            <div className="flex flex-1 flex-col gap-2 p-2.5">
              <textarea
                data-testid="JqFilterBuilder-sample-textarea"
                value={rawJsonInput}
                onChange={(e) => setRawJsonInput(e.target.value)}
                className={`${inputClasses.standard} min-h-[160px] flex-1 resize-none font-mono text-xs`}
              />
              {jsonParseError && (
                <div className="text-sm text-ui-status-error-destructive">
                  {jsonParseError}
                </div>
              )}
            </div>
          )}
          {/* Hidden, not unmounted, while the sample is edited, so the
              "Filter fields…" text survives the editor round-trip. */}
          <div
            className={`flex-1 flex-col ${isEditingJson ? 'hidden' : 'flex'}`}
            style={{ height: CODE_EDITOR_MIN_HEIGHT }}
          >
            <SourceTree
              sampleInput={sampleInput}
              readOnly={readOnly}
              treeRef={sourceTreeRef}
              onEscape={(e) => {
                if (pickActive) {
                  e.preventDefault();
                  cancelKeyboardPick();
                } else if (sourceArmedPath) {
                  e.preventDefault();
                  cancelSourcePick();
                }
              }}
              collapsed={collapsedNodes}
              onToggle={toggleCollapsed}
              dragPathRef={dragPathRef}
              onLeafActivate={activateLeaf}
              pickActive={pickActive}
              armedPathKey={sourceArmedPath?.join('.')}
              mappedTo={mappedTo}
            />
            {(pickActive || sourceArmedPath) && (
              <div className="mt-auto flex items-center gap-1.5 border-t border-ui-divider-lines bg-unlisted-very-light-gray px-3.5 py-2 text-[11px]">
                <span className="font-medium text-ui-cta-primary">
                  {pickActive
                    ? t(
                        'Click or press Enter on a field to select it — Esc to cancel',
                      )
                    : t(
                        'Now click a mapping slot on the right — Esc to cancel',
                      )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Field Mappings / Code Editor Panel ── */}
        <div className={PANEL_CLASSES}>
          <div className={HEADER_CLASSES}>
            <span>{t('Field Mappings')}</span>
            <div className="flex items-center gap-2">
              {isCodeMode && (
                <button
                  type="button"
                  data-testid="JqFilterBuilder-copy-button"
                  onClick={() => {
                    copyToClipboard(codeText, t);
                  }}
                  className="flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                  aria-label={t('Copy to clipboard')}
                >
                  <CopyToClipboard />
                </button>
              )}
              <CodeBuilderToggle
                isCodeMode={isCodeMode}
                onToggle={toggleCodeMode}
                dataTestId="JqFilterBuilder-mode-toggle"
              />
            </div>
          </div>
          {isCodeMode ? (
            <>
              {isComplexJq && (
                <div className="border-b border-ui-divider-lines bg-unlisted-light-blue px-3.5 py-2 text-xs text-unlisted-dark-blue-800">
                  {t(
                    'This filter contains complex expressions. Simplify the filter to use the visual Builder.',
                  )}
                </div>
              )}
              {/* Grows with the filter, never shrinks below the size
                                the panel has always had. No resize grip: the box
                                now sizes itself, and a dragged height would be
                                undone by the next keystroke. */}
              <div
                data-testid="JqFilterBuilder-code-editor-box"
                style={{ height: codeEditorHeight, overflow: 'hidden' }}
              >
                <Editor
                  height="100%"
                  language="jq"
                  value={codeText}
                  theme="vs"
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 13,
                    lineHeight: 20,
                    padding: { top: 8 },
                    readOnly,
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    automaticLayout: true,
                    renderLineHighlight: 'none',
                    fontFamily: MONO_FONT_FAMILY,
                  }}
                  beforeMount={registerJqLanguage}
                  onMount={(e) => {
                    jqEditorRef.current = e;
                    const fit = () =>
                      setCodeEditorHeight(
                        Math.max(CODE_EDITOR_MIN_HEIGHT, e.getContentHeight()),
                      );
                    fit();
                    // Fires for typed lines, wrapped lines and
                    // programmatic value changes alike.
                    e.onDidContentSizeChange(fit);
                  }}
                  onChange={(v) => {
                    codeEditedByUser.current = true;
                    setCodeText(v ?? '');
                    onCodeChange?.(v ?? '');
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto px-3.5 py-3">
              {/* Warning banner for complex jq in builder mode */}
              {isComplexJq && (
                <div className="mb-3 rounded-md bg-unlisted-very-light-orange px-3 py-2 text-xs text-unlisted-orange">
                  {t(
                    'Some fields use complex expressions that can only be edited in Code mode.',
                  )}
                </div>
              )}

              {/* Conditions Section */}
              <div className="mb-4 border-b border-ui-divider-lines pb-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="flex items-center text-greyscale-5"
                    >
                      <Icon icon={ic_filter_list} size={18} />
                    </span>
                    <span className={SECTION_LABEL}>{t('Conditions')}</span>
                    {conditions.length === 0 && (
                      <span className="truncate text-xs leading-5 text-greyscale-5">
                        {t('None. All records pass through.')}
                      </span>
                    )}
                  </div>
                  {!readOnly && (
                    <button
                      onClick={addCondition}
                      className={`${buttonClasses.outlineXs} shrink-0`}
                      type="button"
                    >
                      {t('Add')}
                    </button>
                  )}
                </div>
                {conditions.map((condition, index) => (
                  <ConditionRow
                    key={index}
                    condition={condition}
                    index={index}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={updateCondition}
                    onRemove={removeCondition}
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                  />
                ))}
              </div>

              {/* Core Fields Section */}
              <div className="mb-4">
                <span className={`${SECTION_LABEL} mb-2 block`}>
                  {t('Core Fields')}
                </span>
                {spec ? (
                  spec.fields
                    .filter((f) => f.required || f.showInCore)
                    .map((f) => {
                      if (complexFields[f.key]) {
                        return (
                          <LockedFieldSlot
                            key={f.key}
                            label={f.labelKey}
                            expression={complexFields[f.key]}
                            required={f.required}
                            readOnly={readOnly}
                          />
                        );
                      }
                      return (
                        <DropSlot
                          key={f.key}
                          label={f.labelKey}
                          required={f.required}
                          mapping={coreFieldMaps[f.key] ?? null}
                          sampleInput={sampleInput}
                          readOnly={readOnly}
                          dragPathRef={dragPathRef}
                          onDrop={(p) => updateCoreFieldMap(f.key, p)}
                          onClear={() => updateCoreFieldMap(f.key, null)}
                          onStartPick={startKeyboardPick}
                          sourceArmed={sourceArmedPath != null}
                          transforms={transforms[f.key]}
                          onChangeTransforms={(next) =>
                            setTransformsFor(f.key, next)
                          }
                          transformsLocked={isComplexJq}
                        />
                      );
                    })
                ) : (
                  <div className="text-xs text-ui-text-primary-text">
                    {t('Select an object type to see required fields')}
                  </div>
                )}
              </div>

              {/* Additional Fields Section */}
              <div className="border-t border-ui-divider-lines pt-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className={SECTION_LABEL}>
                    {t('Additional Fields')}
                  </span>
                  {!readOnly && (
                    <button
                      onClick={addAdditionalField}
                      className={buttonClasses.outlineXs}
                      type="button"
                    >
                      {t('Add Field')}
                    </button>
                  )}
                </div>

                {additionalFields.map((field, index) => (
                  <AdditionalRow
                    key={`custom-${index}`}
                    field={field}
                    index={index}
                    sampleInput={sampleInput}
                    readOnly={readOnly}
                    dragPathRef={dragPathRef}
                    onUpdate={updateAdditionalField}
                    onRemove={removeAdditionalField}
                    onStartPick={startKeyboardPick}
                    sourceArmed={sourceArmedPath != null}
                    transformsLocked={isComplexJq}
                  />
                ))}

                {/* Complex additional fields (locked) */}
                {Object.entries(complexFields)
                  .filter(([key]) => key.startsWith('addl:'))
                  .map(([key, expr]) => (
                    <LockedFieldSlot
                      key={key}
                      label={key.slice(5)}
                      expression={expr}
                      readOnly={readOnly}
                    />
                  ))}

                {activeOptionalFields.length === 0 &&
                  additionalFields.length === 0 &&
                  !Object.keys(complexFields).some((k) =>
                    k.startsWith('addl:'),
                  ) && (
                    <div className="text-xs text-ui-text-primary-text">
                      {t('No additional fields')}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Output Preview ── */}
      <div className="mt-3 flex flex-col overflow-hidden rounded-lg border border-ui-divider-lines">
        <div className={`${HEADER_CLASSES} flex-shrink-0`}>
          <span>{t('Output Preview')}</span>
          {!hasMappings && (
            <span className="text-xs font-normal text-ui-text-primary-text">
              {t('Map some fields to see output')}
            </span>
          )}
        </div>
        {hasMappings && evalResult.failedConditions.length > 0 ? (
          <div className="bg-unlisted-very-light-orange px-4 py-3 text-sm">
            <p className="mb-1 font-medium text-unlisted-orange">
              {t('Conditions not met — record would be skipped')}
            </p>
            <ul className="list-inside list-disc text-xs text-ui-text-primary-text">
              {evalResult.failedConditions.map((description, index) => (
                <li key={index} className="font-mono">
                  {description}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {hasMappings && outputErrors.length > 0 && (
              <div className="border-b border-ui-divider-lines bg-unlisted-very-light-orange px-4 py-3">
                <p className="mb-1.5 text-xs font-semibold text-unlisted-orange">
                  {t('Validation errors')}
                </p>
                <ul className="flex flex-col gap-1">
                  {outputErrors.map((error, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-1.5 text-xs text-ui-text-primary-text"
                    >
                      <span className="font-mono text-unlisted-orange">
                        {error.field}
                      </span>
                      <span className="text-ui-text-primary-text">—</span>
                      <span>
                        {String(
                          t(
                            error.messageKey as any,
                            error.messageParams as any,
                          ),
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <pre className="m-0 min-h-[60px] whitespace-pre-wrap bg-greyscale-11 p-4 font-mono text-xs leading-relaxed text-ui-text-primary-text">
              {hasMappings ? JSON.stringify(outputPreview, null, 2) : ''}
            </pre>
          </>
        )}
      </div>
    </div>
  );
};

export default JqFilterBuilder;
